import {
  TeaserTileCarousel as TeaserTileCarouselTypes,
  TeaserTileCarousel_TextPosition,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/stream.types';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {
  TrackPromotionTeaserRecoNormalizer,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  ImageRatio,
  SliderTileWithBadge,
  SliderTiletextPosition,
  Slot,
  TeaserSlider,
} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {NonProductTileSpacingAdjustment} from '../ProductStream/helpers';
import {ShallowRenderer} from '../ProductTile/ShallowRender';
import {ShallowRenderingState} from '../ProductTile/utils';

export const TeaserCarousel: React.FC<
  TeaserTileCarouselTypes & {
    gridColumns?: number;
    streamPosition: number;
    constrainStoryTilesToViewportBoundary?: boolean;
    shallowRenderingState?: ShallowRenderingState;
    uniqueKey?: string;
  }
> = ({
  teaser,
  headline,
  subline,
  textPosition,
  gridColumns,
  streamPosition,
  constrainStoryTilesToViewportBoundary,
  shallowRenderingState,
  uniqueKey,
}) => {
  const trackOriginClick = useTrackOriginClick();

  const entries = teaser.map(element => ({
    to: element.link.linkTarget.url,
    headline: element.headline ?? '',
    subline: element.subline ?? '',
    isPremium: false,
    testId: element.promotionTracking.id,
    image: element.image,
    discoverCtaLabel: element.link.label,
    teaserName: element.promotionTracking.name,
    imageRatio:
      `${element.image.aspectRatio?.width}:${element.image.aspectRatio?.height}` as ImageRatio,
    linkTargetId: element.promotionTracking.linkTargetId,
    tracker: element.tracker,
    promotionTracker: element.promotionTracking,
  }));

  return (
    <StyledSlot Comp="li" columns={gridColumns} position={streamPosition}>
      <ShallowRenderer
        shallowRenderingState={shallowRenderingState}
        id={`teaserCarousel-${streamPosition}`}
        renderer={({ref}: {ref: React.RefObject<HTMLElement | null>}) => (
          <TeaserSlider
            testId="teaserCarousel"
            uniqueKey={uniqueKey}
            sliderRef={ref as React.RefObject<HTMLElement | null>}
            headline={headline}
            subline={subline}
            constrained={constrainStoryTilesToViewportBoundary}
            moveTitleOnTheSide={entries.length <= 2}
            slideCount={1}
            slideCountSmall={2}
            slideCountMedium={entries.length <= 2 ? 2 : 3}
          >
            {entries.map(data => {
              const {testId, isPremium, image, headline, subline, to, tracker} =
                data;

              const handleOnClick = () => {
                // tracking origin should be location ID, and since this is coming from backend it's not typed in the SDK, therefore is casted here to make typescript happy
                trackOriginClick(tracker.name as originClick)();
              };

              return (
                <TrackPromotionTeaserRecoNormalizer
                  streamPosition={streamPosition}
                  recoList={{
                    sectionIndex: streamPosition,
                    recoType: tracker.name,
                  }}
                  data={data}
                  key={testId}
                  Comp="li"
                >
                  <SliderTileWithBadge
                    testId={testId}
                    isPremium={isPremium}
                    image={image}
                    headline={headline}
                    subline={subline}
                    to={to}
                    textsPosition={textPositionMap[textPosition]}
                    imageRatio={data.imageRatio}
                    onClick={handleOnClick}
                  />
                </TrackPromotionTeaserRecoNormalizer>
              );
            })}
          </TeaserSlider>
        )}
      />
    </StyledSlot>
  );
};

const textPositionMap: Record<
  TeaserTileCarousel_TextPosition,
  SliderTiletextPosition
> = {
  [TeaserTileCarousel_TextPosition.UNSPECIFIED]: 'below-image',
  [TeaserTileCarousel_TextPosition.OVER_IMAGE]: 'on-image',
  [TeaserTileCarousel_TextPosition.BELOW_IMAGE]: 'below-image',
};

const StyledSlot = styled(Slot)`
  ${NonProductTileSpacingAdjustment}
`;
